import { CalendarDaysIcon } from "@heroicons/react/20/solid";
import { ClockIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RoleStatus } from "../../../Models";
import useClickOutside from "../../../hooks/useClickOutside";
import useGraphApiToken from "../../../hooks/useGraphApiToken";
import { GenerateMeetingLink, SearchUsers, GraphUser } from "../../../api/microsoft.api";
import { GetInterviewSlots } from "../../../api/roles.api";

type Slot = {
  date: Date | null;
  startTime: Date | null;
  endTime: Date | null;
};

type SelectedInterviewer = {
  id: string;
  displayName: string;
  email: string;
};

type InterviewerGroup = {
  id: string;
  name: string;
  interviewers: SelectedInterviewer[];
  meetingLink: string;
};

type GeneratedSlot = {
  id: string;
  date: Date;
  start: Date;
  end: Date;
  groupId: string;
  groupName: string;
  interviewers: SelectedInterviewer[];
  meetingLink: string;
};

type SlotModalProps = {
  isOpen: boolean;
  errorText: string | null;
  status: RoleStatus[];
  roleId?: string;
  token?: string;
  onClose: () => void;
  onSuccess?: () => void;
  onSubmit: (
    startTime: Date,
    endTime: Date,
    slotDuration: number,
    breakDuration: number,
    meetingLink: string,
    statusId: string,
    interviewerEmails: string[] | null
  ) => Promise<void>;
};

const SlotModal: React.FC<SlotModalProps> = ({
  isOpen,
  errorText,
  status,
  roleId,
  token,
  onClose,
  onSuccess,
  onSubmit,
}) => {
  const [slots, setSlots] = useState<Slot[]>([
    { date: null, startTime: null, endTime: null },
  ]);
  const [slotDuration, setSlotDuration] = useState<number>(0);
  const [breakDuration, setBreakDuration] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [composingMeetingLink, setComposingMeetingLink] = useState<string | null>(null);
  const [isGeneratingComposeLink, setIsGeneratingComposeLink] = useState(false);
  const [regeneratingGroupId, setRegeneratingGroupId] = useState<string | null>(null);
  const [isCheckingOverlap, setIsCheckingOverlap] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<RoleStatus | null>(null);
  const [selectedInterviewers, setSelectedInterviewers] = useState<SelectedInterviewer[]>([]);
  const [interviewerGroups, setInterviewerGroups] = useState<InterviewerGroup[]>([]);
  const [generatedSlots, setGeneratedSlots] = useState<GeneratedSlot[] | null>(null);
  const [isSubmittingAll, setIsSubmittingAll] = useState(false);
  const [interviewerSearch, setInterviewerSearch] = useState("");
  const [searchResults, setSearchResults] = useState<GraphUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const createInterviewRef = useRef(null);
  const GraphApiToken = useGraphApiToken();

  useClickOutside(createInterviewRef, () => onClose());

  useEffect(() => {
    if (errorText != null) setError(errorText);
  }, [errorText, isOpen]);

  useEffect(() => {
    if (interviewerSearch.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      if (!GraphApiToken) return;
      setIsSearching(true);
      try {
        const results = await SearchUsers(GraphApiToken, interviewerSearch);
        setSearchResults(results);
        setShowDropdown(results.length > 0);
      } catch {
        setSearchResults([]);
        setShowDropdown(false);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [interviewerSearch, GraphApiToken]);

  const getCombinedDateTime = (date: Date | null, time: Date | null): Date | null => {
    if (!date || !time) return null;
    const combined = new Date(date);
    combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return combined;
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = status.find((s) => s.id === e.target.value);
    setSelectedStatus(selected || null);
  };

  const addSlot = () => {
    setSlots([...slots, { date: null, startTime: null, endTime: null }]);
  };

  const removeSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSlot = (index: number, field: keyof Slot, value: Date | null) => {
    const updated = [...slots];
    updated[index][field] = value;
    setSlots(updated);
  };

  const addInterviewerGroup = () => {
    if (!composingMeetingLink?.trim()) return;
    const name =
      selectedInterviewers.length > 0
        ? selectedInterviewers.map((p) => p.email).join(", ")
        : `Group ${interviewerGroups.length + 1}`;
    setInterviewerGroups((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name,
        interviewers: selectedInterviewers,
        meetingLink: composingMeetingLink.trim(),
      },
    ]);
    setSelectedInterviewers([]);
    setComposingMeetingLink(null);
  };

  const removeInterviewerGroup = (id: string) => {
    setInterviewerGroups((prev) => prev.filter((g) => g.id !== id));
  };

  const updateGroupMeetingLink = (groupId: string, link: string) => {
    setInterviewerGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, meetingLink: link } : g))
    );
  };

  // Every group's slots span the same set of date/time ranges (groups run in parallel),
  // so one combined min-start/max-end span is what gets passed to the Graph "create
  // meeting" call — shown to the recruiter as a "review before generating" hint too.
  const computeOverallTimeRange = (): { start: Date; end: Date } | null => {
    const allStarts = slots
      .map((s) => getCombinedDateTime(s.date, s.startTime))
      .filter(Boolean) as Date[];
    const allEnds = slots
      .map((s) => getCombinedDateTime(s.date, s.endTime))
      .filter(Boolean) as Date[];
    if (allStarts.length === 0 || allEnds.length === 0) return null;
    return {
      start: new Date(Math.min(...allStarts.map((d) => d.getTime()))),
      end: new Date(Math.max(...allEnds.map((d) => d.getTime()))),
    };
  };

  // Breaks the configured time-slot ranges into the actual slotDuration-sized chunks
  // that will be generated, grouped by date — shown above Interview Groups so the
  // recruiter can review exact slot timings before adding groups/links.
  const computeSlotTimingPreview = (): { dateLabel: string; ranges: string[] }[] => {
    if (!slotDuration || slotDuration <= 0) return [];
    const byDate: Record<string, { dateObj: Date; ranges: string[] }> = {};
    const timeFmt = (d: Date) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

    for (const slot of slots) {
      const combinedStart = getCombinedDateTime(slot.date, slot.startTime);
      const combinedEnd = getCombinedDateTime(slot.date, slot.endTime);
      if (!combinedStart || !combinedEnd || combinedEnd <= combinedStart) continue;

      let cursor = new Date(combinedStart);
      while (new Date(cursor.getTime() + slotDuration * 60000) <= combinedEnd) {
        const slotStart = new Date(cursor);
        const slotEnd = new Date(cursor.getTime() + slotDuration * 60000);
        const dateKey = slotStart.toDateString();
        if (!byDate[dateKey]) byDate[dateKey] = { dateObj: slotStart, ranges: [] };
        byDate[dateKey].ranges.push(`${timeFmt(slotStart)}–${timeFmt(slotEnd)}`);
        cursor = new Date(slotEnd.getTime() + breakDuration * 60000);
      }
    }

    return Object.values(byDate)
      .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime())
      .map((entry) => ({
        dateLabel: entry.dateObj.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        ranges: entry.ranges,
      }));
  };

  const generatePreview = async () => {
    if (!selectedStatus) {
      setError("Status is required.");
      return;
    }
    if (interviewerGroups.length === 0) {
      setError("Add at least one interview group with a meeting link before previewing.");
      return;
    }
    if (!slotDuration || slotDuration <= 0) {
      setError("Slot duration must be greater than zero.");
      return;
    }
    if (breakDuration < 0) {
      setError("Break duration cannot be negative.");
      return;
    }

    const ranges: { start: Date; end: Date }[] = [];
    for (let i = 0; i < slots.length; i++) {
      const { date, startTime, endTime } = slots[i];
      const combinedStart = getCombinedDateTime(date, startTime);
      const combinedEnd = getCombinedDateTime(date, endTime);

      if (!combinedStart || !combinedEnd || combinedEnd <= combinedStart) {
        setError(`Invalid start/end time for slot #${i + 1}`);
        return;
      }

      const totalMinutes = (combinedEnd.getTime() - combinedStart.getTime()) / 60000;
      if (slotDuration > totalMinutes) {
        setError(`Invalid slot duration for slot #${i + 1}`);
        return;
      }

      ranges.push({ start: combinedStart, end: combinedEnd });
    }

    const generated: GeneratedSlot[] = [];
    ranges.forEach((range) => {
      interviewerGroups.forEach((group) => {
        let cursor = new Date(range.start);
        while (new Date(cursor.getTime() + slotDuration * 60000) <= range.end) {
          const slotStart = new Date(cursor);
          const slotEnd = new Date(cursor.getTime() + slotDuration * 60000);
          generated.push({
            id: crypto.randomUUID(),
            date: slotStart,
            start: slotStart,
            end: slotEnd,
            groupId: group.id,
            groupName: group.name,
            interviewers: group.interviewers,
            meetingLink: group.meetingLink,
          });
          cursor = new Date(slotEnd.getTime() + breakDuration * 60000);
        }
      });
    });

    if (generated.length === 0) {
      setError("No slots could be generated with the current settings.");
      return;
    }

    setError(null);
    setIsCheckingOverlap(true);
    try {
      const conflicts = await findOverlapConflicts(generated);
      if (conflicts.length > 0) {
        const preview = conflicts.slice(0, 5).join("; ");
        const more = conflicts.length > 5 ? ` (+${conflicts.length - 5} more)` : "";
        setError(`Overlap detected — fix before previewing: ${preview}${more}`);
        return;
      }
      setGeneratedSlots(generated);
    } catch (err: any) {
      setError(err.message || "Could not validate slot overlaps. Please try again.");
    } finally {
      setIsCheckingOverlap(false);
    }
  };

  // Two slots conflict only if they share an interviewer AND their times overlap —
  // groups are meant to run in parallel with different interviewers, so overlapping
  // times alone (across different interviewers) is normal, not a conflict.
  const findOverlapConflicts = async (generated: GeneratedSlot[]): Promise<string[]> => {
    const conflicts: string[] = [];
    const timeFmt = (d: Date) =>
      d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    const emailsOf = (gs: GeneratedSlot) => gs.interviewers.map((p) => p.email.toLowerCase());
    const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) =>
      aStart < bEnd && aEnd > bStart;
    const sharedEmail = (a: string[], b: string[]) => a.find((e) => b.includes(e));

    for (let i = 0; i < generated.length; i++) {
      for (let j = i + 1; j < generated.length; j++) {
        const a = generated[i];
        const b = generated[j];
        if (!overlaps(a.start, a.end, b.start, b.end)) continue;
        const shared = sharedEmail(emailsOf(a), emailsOf(b));
        if (shared) {
          conflicts.push(
            `${shared} is double-booked in "${a.groupName}" and "${b.groupName}" at ${timeFmt(a.start)}`
          );
        }
      }
    }

    if (roleId && token) {
      const existingSlots = await GetInterviewSlots(roleId, token);
      for (const gs of generated) {
        const gsEmails = emailsOf(gs);
        if (gsEmails.length === 0) continue;
        for (const existing of existingSlots) {
          const existingStart = new Date(existing.startTime);
          const existingEnd = new Date(existing.endTime);
          if (!overlaps(gs.start, gs.end, existingStart, existingEnd)) continue;
          const existingEmails = (existing.interviewerEmail || "")
            .split(",")
            .map((e) => e.trim().toLowerCase())
            .filter(Boolean);
          const shared = sharedEmail(gsEmails, existingEmails);
          if (shared) {
            conflicts.push(
              `${shared} already has a slot at ${timeFmt(existingStart)} conflicting with "${gs.groupName}"`
            );
          }
        }
      }
    }

    return conflicts;
  };

  const removeGeneratedSlot = (id: string) => {
    setGeneratedSlots((prev) => (prev ? prev.filter((s) => s.id !== id) : prev));
  };

  const removeGeneratedGroup = (groupId: string) => {
    setGeneratedSlots((prev) => (prev ? prev.filter((s) => s.groupId !== groupId) : prev));
  };

  const handleConfirmGenerated = async () => {
    if (!generatedSlots || generatedSlots.length === 0 || !selectedStatus) return;
    setIsSubmittingAll(true);
    try {
      for (const gs of generatedSlots) {
        const interviewerEmailsArray: string[] | null =
          gs.interviewers.length > 0 ? gs.interviewers.map((p) => p.email).filter(Boolean) : null;

        await onSubmit(
          gs.start,
          gs.end,
          slotDuration,
          0,
          gs.meetingLink,
          selectedStatus.id,
          interviewerEmailsArray
        );
      }

      onSuccess?.();
      handleResetFields();
      onClose();
    } catch (er) {
    } finally {
      setIsSubmittingAll(false);
    }
  };

  const handleResetFields = () => {
    setSlots([{ date: null, startTime: null, endTime: null }]);
    setSlotDuration(0);
    setBreakDuration(0);
    setError(null);
    setComposingMeetingLink(null);
    setSelectedInterviewers([]);
    setInterviewerGroups([]);
    setGeneratedSlots(null);
    setInterviewerSearch("");
    setSearchResults([]);
    setShowDropdown(false);
    setSelectedStatus(null);
  };

  const handleClose = () => {
    handleResetFields();
    onClose();
  };

  const addInterviewer = (user: GraphUser) => {
    const email = user.mail || user.userPrincipalName;
    if (!email) return;
    if (selectedInterviewers.some((p) => p.id === user.id)) return;
    setSelectedInterviewers((prev) => [
      ...prev,
      { id: user.id, displayName: user.displayName, email },
    ]);
    setInterviewerSearch("");
    setShowDropdown(false);
    setSearchResults([]);
  };

  const removeInterviewer = (id: string) => {
    setSelectedInterviewers((prev) => prev.filter((p) => p.id !== id));
  };

  const getMinTime = (slotDate: Date | null) => {
    if (!slotDate) return new Date(0, 0, 0, 0, 0, 0);
    const today = new Date();
    const isToday =
      slotDate.getDate() === today.getDate() &&
      slotDate.getMonth() === today.getMonth() &&
      slotDate.getFullYear() === today.getFullYear();
    if (isToday) return today;
    return new Date(0, 0, 0, 0, 0, 0);
  };

  const getExcludedTimes = (currentIndex: number, slotDate: Date | null) => {
    if (!slotDate) return [];
    const excluded: Date[] = [];
    slots.forEach((s, idx) => {
      if (idx !== currentIndex && s.date && s.startTime && s.endTime) {
        const sameDay =
          s.date.getDate() === slotDate.getDate() &&
          s.date.getMonth() === slotDate.getMonth() &&
          s.date.getFullYear() === slotDate.getFullYear();
        if (sameDay) {
          let t = new Date(s.startTime);
          while (t <= s.endTime) {
            excluded.push(new Date(t));
            t = new Date(t.getTime() + 15 * 60000);
          }
        }
      }
    });
    return excluded;
  };

  const generateLinkForCompose = async () => {
    try {
      setIsGeneratingComposeLink(true);
      const graphToken = GraphApiToken;
      if (!graphToken) { setIsGeneratingComposeLink(false); return; }

      const range = computeOverallTimeRange();
      if (!range) {
        setError("Add at least one time slot before generating a meeting link.");
        return;
      }

      const subject =
        selectedInterviewers.length > 0
          ? `Entnt Interview - ${selectedInterviewers.map((p) => p.displayName).join(", ")}`
          : "Entnt Interview Meeting";
      const link = await GenerateMeetingLink(graphToken, subject, range.start, range.end);

      if (link) setComposingMeetingLink(link);
      else setError("Could not create meeting link.");
    } catch (err: any) {
      setError(err.message || "Error creating meeting link");
    } finally {
      setIsGeneratingComposeLink(false);
    }
  };

  const generateLinkForGroup = async (group: InterviewerGroup) => {
    try {
      setRegeneratingGroupId(group.id);
      const graphToken = GraphApiToken;
      if (!graphToken) { setRegeneratingGroupId(null); return; }

      const range = computeOverallTimeRange();
      if (!range) {
        setError("Add at least one time slot before generating a meeting link.");
        return;
      }

      const subject =
        group.interviewers.length > 0
          ? `Entnt Interview - ${group.interviewers.map((p) => p.displayName).join(", ")}`
          : `Entnt Interview - ${group.name}`;
      const link = await GenerateMeetingLink(graphToken, subject, range.start, range.end);

      if (link) updateGroupMeetingLink(group.id, link);
      else setError("Could not create meeting link.");
    } catch (err: any) {
      setError(err.message || "Error creating meeting link");
    } finally {
      setRegeneratingGroupId(null);
    }
  };

  if (!isOpen) return null;

  // Shared input classes — compact, clean
  const inputCls =
    "w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#0d42ec]/30 focus:border-[#0d42ec] transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500";

  const labelCls = "block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5";

  const slotTimingPreview = computeSlotTimingPreview();

  const sortedGeneratedSlots = generatedSlots
    ? [...generatedSlots].sort(
        (a, b) => a.start.getTime() - b.start.getTime() || a.groupName.localeCompare(b.groupName)
      )
    : [];

  const generatedByDate = sortedGeneratedSlots.reduce<Record<string, Record<string, GeneratedSlot[]>>>((acc, gs) => {
    const dateKey = gs.date.toDateString();
    acc[dateKey] = acc[dateKey] || {};
    (acc[dateKey][gs.groupId] = acc[dateKey][gs.groupId] || []).push(gs);
    return acc;
  }, {});

  const interviewerSearchBox = (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="text"
        value={interviewerSearch}
        onChange={(e) => setInterviewerSearch(e.target.value)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        className={`${inputCls} pl-9 pr-8`}
        placeholder="Search by name…"
      />
      {isSearching && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <svg className="animate-spin h-3.5 w-3.5 text-[#0d42ec]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      )}

      {showDropdown && (
        <div
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {searchResults.filter((r) => !selectedInterviewers.some((s) => s.id === r.id)).length === 0 ? (
            <div className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
              No matching users found
            </div>
          ) : (
            <ul className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
              {searchResults
                .filter((r) => !selectedInterviewers.some((s) => s.id === r.id))
                .map((user) => (
                  <li
                    key={user.id}
                    onMouseDown={() => addInterviewer(user)}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#0d42ec]/5 cursor-pointer transition-colors"
                  >
                    <div className="w-7 h-7 bg-[#0d42ec] rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {user.displayName?.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                        {user.displayName}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {user.mail || user.userPrincipalName}
                      </p>
                    </div>
                    {user.department && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded flex-shrink-0">
                        {user.department}
                      </span>
                    )}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50 p-4">
      <div
        ref={createInterviewRef}
        className="bg-white dark:bg-gray-950 rounded-xl shadow-2xl w-full max-w-xl max-h-[88vh] overflow-y-auto border border-gray-200 dark:border-gray-800"
      >
        {/* ── Header ── */}
        <div className="sticky top-0 z-20 bg-white dark:bg-gray-950 px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-[#0d42ec] rounded-lg">
                <CalendarDaysIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                  Create Interview Slots
                </h2>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {generatedSlots ? "Review generated slots before creating them" : "Schedule your interview sessions"}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400" />
            </button>
          </div>

          {!generatedSlots && (
            <div className="mt-3">
              <label className={labelCls}>
                Stage <span className="text-[#0d42ec] normal-case tracking-normal font-normal">*</span>
              </label>
              <div className="relative">
                <select
                  value={selectedStatus?.id || ""}
                  onChange={handleStatusChange}
                  className={`${inputCls} appearance-none pr-8 cursor-pointer`}
                >
                  <option value="">Choose a stage…</option>
                  {status.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          )}
        </div>

        {generatedSlots === null ? (
          <>
            {/* ── Body (configuration) ── */}
            <div className="px-5 py-4 space-y-5">

              {/* Time Slots */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`${labelCls} mb-0 flex items-center gap-1.5`}>
                    <ClockIcon className="w-3.5 h-3.5" />
                    Time Slots
                  </label>
                </div>

                <div className="space-y-3">
                  {slots.map((slot, index) => (
                    <div
                      key={index}
                      className="p-3.5 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 bg-[#0d42ec] rounded-md flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                            {index + 1}
                          </span>
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Slot Group #{index + 1}
                          </span>
                        </div>
                        {slots.length > 1 && (
                          <button
                            onClick={() => removeSlot(index)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>

                      <div className="space-y-2.5">
                        <div>
                          <label className={labelCls}>Date</label>
                          <DatePicker
                            selected={slot.date}
                            onChange={(date) => updateSlot(index, "date", date)}
                            minDate={new Date()}
                            dateFormat="MMMM d, yyyy"
                            className={inputCls}
                            placeholderText="Select date"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2.5">
                          <div>
                            <label className={labelCls}>Start Time</label>
                            <DatePicker
                              selected={slot.startTime}
                              onChange={(time) => updateSlot(index, "startTime", time)}
                              showTimeSelect showTimeSelectOnly
                              timeIntervals={15}
                              dateFormat="h:mm aa"
                              minTime={getMinTime(slot.date)}
                              maxTime={new Date(0, 0, 0, 23, 45)}
                              excludeTimes={getExcludedTimes(index, slot.date)}
                              disabled={!slot.date}
                              className={inputCls}
                              placeholderText={slot.date ? "Start time" : "Select date first"}
                            />
                          </div>
                          <div>
                            <label className={labelCls}>End Time</label>
                            <DatePicker
                              selected={slot.endTime}
                              onChange={(time) => updateSlot(index, "endTime", time)}
                              showTimeSelect showTimeSelectOnly
                              timeIntervals={15}
                              dateFormat="h:mm aa"
                              minTime={getMinTime(slot.date)}
                              maxTime={new Date(0, 0, 0, 23, 45)}
                              excludeTimes={getExcludedTimes(index, slot.date)}
                              disabled={!slot.date}
                              className={inputCls}
                              placeholderText={slot.date ? "End time" : "Select date first"}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={addSlot}
                  className="mt-2.5 w-full py-2 border border-dashed border-[#0d42ec]/40 rounded-lg text-[#0d42ec] text-sm font-medium hover:bg-[#0d42ec]/5 transition-colors flex items-center justify-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Another Time Slot
                </button>
              </div>

              {/* Duration Settings */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>
                    Slot Duration <span className="text-[#0d42ec] normal-case tracking-normal font-normal">(min) *</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={slotDuration}
                    onChange={(e) => setSlotDuration(Math.max(0, Number(e.target.value) || 0))}
                    className={`${inputCls} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    placeholder="e.g. 60"
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Break Duration <span className="normal-case tracking-normal font-normal text-gray-400">(min)</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={breakDuration}
                    onChange={(e) => setBreakDuration(Math.max(0, Number(e.target.value) || 0))}
                    className={`${inputCls} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    placeholder="e.g. 15"
                  />
                </div>
              </div>

              {/* Slots to be created — review before adding groups/links */}
              {slotTimingPreview.length > 0 && (
                <div className="p-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg space-y-1">
                  <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Slots to be created
                  </p>
                  {slotTimingPreview.map((entry) => (
                    <p key={entry.dateLabel} className="text-[11px] text-gray-500 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{entry.dateLabel}</span>
                      {" - "}
                      {entry.ranges.join(", ")}
                    </p>
                  ))}
                </div>
              )}

              {/* Interview Groups — each group gets its own meeting link, since groups run
                  in parallel and must not join the same call. */}
              <div>
                <label className={labelCls}>Interview Groups</label>

                {interviewerGroups.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {interviewerGroups.map((group) => (
                      <div
                        key={group.id}
                        className="p-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg space-y-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">{group.name}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {group.interviewers.length > 0
                                ? group.interviewers.map((p) => p.displayName).join(", ")
                                : "No interviewers"}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeInterviewerGroup(group.id)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors flex-shrink-0"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={group.meetingLink}
                            onChange={(e) => updateGroupMeetingLink(group.id, e.target.value)}
                            className={`${inputCls} flex-1 text-xs`}
                            placeholder="Meeting link"
                          />
                          <button
                            type="button"
                            onClick={() => generateLinkForGroup(group)}
                            disabled={regeneratingGroupId === group.id}
                            className="px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-[#0d42ec]/40 hover:text-[#0d42ec] text-gray-500 dark:text-gray-300 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {regeneratingGroupId === group.id ? "…" : "Regenerate"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="p-3 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg space-y-2.5">
                  {selectedInterviewers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedInterviewers.map((person) => (
                        <div
                          key={person.id}
                          className="flex items-center gap-1.5 px-2.5 py-1 bg-[#0d42ec]/8 border border-[#0d42ec]/20 rounded-full"
                        >
                          <div className="w-4 h-4 bg-[#0d42ec] rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">
                            {person.displayName.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-xs font-medium text-[#0d42ec] dark:text-blue-300">
                            {person.displayName}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeInterviewer(person.id)}
                            className="text-[#0d42ec]/50 hover:text-red-500 transition-colors"
                            aria-label={`Remove ${person.displayName}`}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {interviewerSearchBox}

                  <div>
                    <label className={labelCls}>
                      Meeting Link <span className="text-[#0d42ec] normal-case tracking-normal font-normal">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={composingMeetingLink || ""}
                        onChange={(e) => setComposingMeetingLink(e.target.value)}
                        className={`${inputCls} flex-1`}
                        placeholder="Click Generate, or paste a link"
                      />
                      <button
                        type="button"
                        onClick={generateLinkForCompose}
                        disabled={isGeneratingComposeLink}
                        className="px-3.5 py-2 bg-[#0d42ec] hover:bg-[#0b38cc] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap"
                      >
                        {isGeneratingComposeLink ? (
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                        ) : "Generate"}
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={addInterviewerGroup}
                    disabled={!composingMeetingLink?.trim()}
                    className="w-full py-2 border border-[#0d42ec]/40 rounded-lg text-[#0d42ec] text-sm font-medium hover:bg-[#0d42ec]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Interview Group
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-red-600 dark:text-red-400 text-xs font-medium">{error}</span>
                </div>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-2.5">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={generatePreview}
                disabled={isCheckingOverlap}
                className="px-5 py-2 text-sm font-semibold text-white bg-[#0d42ec] hover:bg-[#0b38cc] rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isCheckingOverlap ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Checking for overlaps…
                  </>
                ) : "Preview Slots"}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* ── Body (preview / overview) ── */}
            <div className="px-5 py-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {generatedSlots.length} slot{generatedSlots.length === 1 ? "" : "s"} will be created
                </p>
                <button
                  type="button"
                  onClick={() => setGeneratedSlots(null)}
                  className="text-xs font-medium text-[#0d42ec] hover:underline"
                >
                  ← Back to edit
                </button>
              </div>

              {Object.entries(generatedByDate).map(([dateKey, groupsMap]) => (
                <div key={dateKey}>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                    {new Date(dateKey).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                  </p>
                  <div className="space-y-3">
                    {Object.entries(groupsMap).map(([groupId, items]) => (
                      <div key={groupId}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                            {items[0].groupName} · {items.length} slot{items.length === 1 ? "" : "s"}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeGeneratedGroup(groupId)}
                            className="text-[10px] font-medium text-red-500 hover:underline"
                          >
                            Remove group
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-400 truncate mb-1.5" title={items[0].meetingLink}>
                          {items[0].meetingLink}
                        </p>
                        <div className="space-y-1.5">
                          {items.map((gs) => (
                            <div
                              key={gs.id}
                              className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                                  {gs.start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – {gs.end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                                </p>
                                <p className="text-xs text-gray-400 truncate">
                                  {gs.interviewers.length > 0 ? gs.interviewers.map((p) => p.displayName).join(", ") : "No interviewers"}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeGeneratedSlot(gs.id)}
                                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors flex-shrink-0"
                                aria-label="Remove slot"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {generatedSlots.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">All generated slots have been removed.</p>
              )}

              {error && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-red-600 dark:text-red-400 text-xs font-medium">{error}</span>
                </div>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-2.5">
              <button
                onClick={() => setGeneratedSlots(null)}
                disabled={isSubmittingAll}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleConfirmGenerated}
                disabled={isSubmittingAll || generatedSlots.length === 0}
                className="px-5 py-2 text-sm font-semibold text-white bg-[#0d42ec] hover:bg-[#0b38cc] rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isSubmittingAll ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Creating…
                  </>
                ) : (
                  `Confirm & Create (${generatedSlots.length})`
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SlotModal;
